import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth'
import { auth } from '@/firebase'
import { getUser, createUser } from '@/firebase/collections'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const userData = ref(null)
  const loading = ref(true)
  const error = ref(null)

  const isAuthenticated = computed(() => !!user.value)
  const hasCompletedProfile = computed(() => {
    return userData.value?.companyName && userData.value?.phoneNumber
  })

  // Initialize auth state listener
  const initAuth = () => {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          user.value = firebaseUser

          // Load user data from Firestore
          try {
            userData.value = await getUser(firebaseUser.uid)

            // If user doesn't exist in Firestore, create default profile
            if (!userData.value) {
              // Check for pending consent data (from Google sign-in)
              const pendingConsent = sessionStorage.getItem('pendingConsent')
              const consentData = pendingConsent ? JSON.parse(pendingConsent) : {}
              sessionStorage.removeItem('pendingConsent')

              await createUser(firebaseUser.uid, {
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName || '',
                photoURL: firebaseUser.photoURL || '',
                companyName: '',
                phoneNumber: '',
                calendlyUrl: '',
                subscription: {
                  tier: 'free',
                  creditsRemaining: 10,
                  renewalDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                },
                settings: {
                  defaultVoiceId: 'gs0tAILXbY5DNrJrsM6F',
                  followUpEnabled: true,
                  followUpDelay4h: true,
                  followUpDelay24h: true
                },
                consent: {
                  termsAccepted: consentData.termsAccepted || false,
                  termsAcceptedAt: consentData.termsAcceptedAt || null,
                  termsVersion: consentData.termsVersion || '1.0',
                  privacyAccepted: consentData.privacyAccepted || false,
                  privacyAcceptedAt: consentData.privacyAcceptedAt || null,
                  privacyVersion: consentData.privacyVersion || '1.0',
                  ipAddress: null
                }
              })
              userData.value = await getUser(firebaseUser.uid)
            }
          } catch (err) {
            console.error('Error loading user data:', err)
            error.value = err.message
          }
        } else {
          user.value = null
          userData.value = null
        }
        loading.value = false
        resolve()
      })
    })
  }

  // Email/Password Sign In
  const signIn = async (email, password) => {
    try {
      error.value = null
      const result = await signInWithEmailAndPassword(auth, email, password)
      return result.user
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  // Email/Password Sign Up
  const signUp = async (email, password, displayName, consentData = {}) => {
    try {
      error.value = null
      const result = await createUserWithEmailAndPassword(auth, email, password)

      // Create user profile in Firestore with consent records
      await createUser(result.user.uid, {
        uid: result.user.uid,
        email,
        displayName,
        photoURL: '',
        companyName: '',
        phoneNumber: '',
        calendlyUrl: '',
        subscription: {
          tier: 'free',
          creditsRemaining: 10,
          renewalDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        },
        settings: {
          defaultVoiceId: 'gs0tAILXbY5DNrJrsM6F',
          followUpEnabled: true,
          followUpDelay4h: true,
          followUpDelay24h: true
        },
        consent: {
          termsAccepted: consentData.termsAccepted || false,
          termsAcceptedAt: consentData.termsAcceptedAt || null,
          termsVersion: consentData.termsVersion || '1.0',
          privacyAccepted: consentData.privacyAccepted || false,
          privacyAcceptedAt: consentData.privacyAcceptedAt || null,
          privacyVersion: consentData.privacyVersion || '1.0',
          ipAddress: null // Could be captured if needed
        }
      })

      return result.user
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  // Google Sign In
  const signInWithGoogle = async (consentData = {}) => {
    try {
      error.value = null
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)

      // Store consent data in a temporary variable to be picked up by auth state listener
      if (consentData.termsAccepted) {
        sessionStorage.setItem('pendingConsent', JSON.stringify(consentData))
      }

      return result.user
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  // Sign Out
  const logout = async () => {
    try {
      error.value = null
      await signOut(auth)
      user.value = null
      userData.value = null
    } catch (err) {
      error.value = err.message
      throw err
    }
  }

  // Update user data
  const refreshUserData = async () => {
    if (user.value) {
      userData.value = await getUser(user.value.uid)
    }
  }

  return {
    user,
    userData,
    loading,
    error,
    isAuthenticated,
    hasCompletedProfile,
    initAuth,
    signIn,
    signUp,
    signInWithGoogle,
    logout,
    refreshUserData
  }
})
